<script lang="ts">
	import type { Device } from 'src/types/Device';
	import { format } from 'svelte-i18n';
	import { capFirstChar } from 'src/helpers/utilities';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import {
		ButtonV2,
		Container,
		SliderV2,
		Text,
		ToggleV2,
		TokenV2,
	} from '@Shelly/shelly-design-system';
	import SettingGuide from 'src/components/DevicesSettings/SettingGuide.svelte';
	import CameraStreamPreview from './CameraStreamPreview.svelte';
	import { getCameraConfig, saveCameraConfig } from '../../helpers/camera-settings-helpers';
	import { hasCameraConfigKey } from '../../helpers/camera-features';
	import type {
		CameraAntiflicker,
		CameraImageAdjustmentSliderKey,
		CameraImageAdjustmentsState,
		CameraVideoConfig,
		CameraVideoOrientationState,
	} from '../../types/camera-settings';

	export let device: Device;

	type PictureState = CameraVideoOrientationState &
		CameraImageAdjustmentsState & { antiflicker: CameraAntiflicker };

	type VideoSlider = {
		key: CameraImageAdjustmentSliderKey;
		title: string;
		description: string;
		min: number;
		max: number;
		defaultValue: number;
		lineMarkers: { value: number; displayValue?: string; showLabel: boolean }[];
		supported: boolean;
	};

	const DEFAULT_ANTIFLICKER: CameraAntiflicker = '50Hz';

	const DEFAULT_MARKERS = [
		{ value: 0, showLabel: true },
		{ value: 50, showLabel: true },
		{ value: 100, showLabel: true },
	];

	const TINT_MARKERS = [
		{
			value: -256,
			displayValue: $format('camera_video_tint_magenta', { default: 'Magenta' }),
			showLabel: true,
		},
		{ value: -192, showLabel: false },
		{ value: -128, showLabel: false },
		{ value: -64, showLabel: false },
		{ value: 0, showLabel: true },
		{ value: 64, showLabel: false },
		{ value: 128, showLabel: false },
		{ value: 192, showLabel: false },
		{
			value: 256,
			displayValue: $format('camera_video_tint_green', { default: 'Green' }),
			showLabel: true,
		},
	];

	const TEMPERATURE_MARKERS = [
		{
			value: -256,
			displayValue: $format('camera_video_temperature_cold', { default: 'Cold' }),
			showLabel: true,
		},
		{ value: -192, showLabel: false },
		{ value: -128, showLabel: false },
		{ value: -64, showLabel: false },
		{
			value: 0,
			displayValue: $format('camera_video_temperature_neutral', { default: 'Neutral' }),
			showLabel: true,
		},
		{ value: 64, showLabel: false },
		{ value: 128, showLabel: false },
		{ value: 192, showLabel: false },
		{
			value: 256,
			displayValue: $format('camera_video_temperature_warm', { default: 'Warm' }),
			showLabel: true,
		},
	];

	const supportsAntiflicker = hasCameraConfigKey(device, 'video.antiflicker');

	const VIDEO_SLIDERS: VideoSlider[] = (
		[
			{
				key: 'brightness',
				title: $format('camera_video_brightness', { default: 'Brightness' }),
				description: $format('settings_desc_camera_brightness', {
					default: 'Make the image lighter or darker.',
				}),
				min: 0,
				max: 100,
				defaultValue: 50,
				lineMarkers: DEFAULT_MARKERS,
				supported: true,
			},
			{
				key: 'contrast',
				title: $format('camera_video_contrast', { default: 'Contrast' }),
				description: $format('settings_desc_camera_contrast', {
					default: 'Increase the difference between light and dark areas.',
				}),
				min: 0,
				max: 100,
				defaultValue: 50,
				lineMarkers: DEFAULT_MARKERS,
				supported: true,
			},
			{
				key: 'saturation',
				title: $format('camera_video_saturation', { default: 'Saturation' }),
				description: $format('settings_desc_camera_saturation', {
					default: 'Make colours more or less intense.',
				}),
				min: 0,
				max: 100,
				defaultValue: 50,
				lineMarkers: DEFAULT_MARKERS,
				supported: true,
			},
			{
				key: 'sharpness',
				title: $format('camera_video_sharpness', { default: 'Sharpness' }),
				description: $format('settings_desc_camera_sharpness', {
					default: 'Increase edge detail in the image.',
				}),
				min: 0,
				max: 100,
				defaultValue: 50,
				lineMarkers: DEFAULT_MARKERS,
				supported: true,
			},
			{
				key: 'tint',
				title: $format('camera_video_tint', { default: 'Tint' }),
				description: $format('settings_desc_camera_tint', {
					default: 'Shift colour between magenta and green.',
				}),
				min: -256,
				max: 256,
				defaultValue: 0,
				lineMarkers: TINT_MARKERS,
				supported: hasCameraConfigKey(device, 'video.tint'),
			},
			{
				key: 'temperature',
				title: $format('camera_video_temperature', { default: 'Color temperature' }),
				description: $format('settings_desc_camera_temperature', {
					default: 'Shift colour between cool (blue) and warm (orange).',
				}),
				min: -256,
				max: 256,
				defaultValue: 0,
				lineMarkers: TEMPERATURE_MARKERS,
				supported: hasCameraConfigKey(device, 'video.temperature'),
			},
		] as VideoSlider[]
	).filter((slider) => slider.supported);

	const readPicture = (): PictureState => {
		const raw = getCameraConfig(device)?.video;
		const state = {
			flip: typeof raw?.flip === 'boolean' ? raw.flip : false,
			mirror: typeof raw?.mirror === 'boolean' ? raw.mirror : false,
			antiflicker: raw?.antiflicker ?? DEFAULT_ANTIFLICKER,
		} as PictureState;
		for (const { key, defaultValue } of VIDEO_SLIDERS) {
			state[key] = typeof raw?.[key] === 'number' ? raw[key] : defaultValue;
		}
		return state;
	};

	let picture: PictureState = readPicture();
	let cache: PictureState = { ...picture };
	let loading = false;

	$: orientationDirty = picture.flip !== cache.flip || picture.mirror !== cache.mirror;
	$: antiflickerDirty = supportsAntiflicker && picture.antiflicker !== cache.antiflicker;
	$: slidersDirty = VIDEO_SLIDERS.some(({ key }) => picture[key] !== cache[key]);
	$: isDirty = orientationDirty || antiflickerDirty || slidersDirty;

	const buildConfig = (): CameraVideoConfig => {
		const config: CameraVideoConfig = {};
		if (orientationDirty) {
			config.flip = picture.flip;
			config.mirror = picture.mirror;
		}
		if (antiflickerDirty) config.antiflicker = picture.antiflicker;
		for (const { key } of VIDEO_SLIDERS) {
			if (picture[key] !== cache[key]) config[key] = picture[key];
		}
		return config;
	};

	const save = async () => {
		if (!isDirty || loading) return;
		loading = true;
		const currentVideo = getCameraConfig(device)?.video ?? {};
		const success = await saveCameraConfig(
			device,
			{ video: { ...currentVideo, ...buildConfig() } },
			$format('setting_scc_saved_toast', { default: 'Setting saved successfully.' }),
		);
		if (success) {
			cache = { ...picture };
		} else {
			picture = { ...cache };
		}
		loading = false;
	};

	const resetToDefaults = async () => {
		if (loading) return;
		loading = true;
		const currentVideo = getCameraConfig(device)?.video ?? {};
		const resetVideo: CameraVideoConfig = { ...currentVideo, flip: null, mirror: null };
		if (supportsAntiflicker) resetVideo.antiflicker = null;
		for (const { key } of VIDEO_SLIDERS) {
			resetVideo[key] = null;
		}
		const success = await saveCameraConfig(
			device,
			{ video: resetVideo },
			$format('camera_video_reset_success', { default: 'Video settings reset to defaults.' }),
		);
		if (success) {
			picture = readPicture();
			cache = { ...picture };
		}
		loading = false;
	};

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		setStickyBottomAction({
			onClick: save,
			loading,
			disabled: loading || !isDirty,
			icon: 'fas fa-save',
		});
	}
</script>

<SettingGuide
	text={$format('settings_guide_camera_picture', {
		default:
			'Flip or mirror the image, reduce flicker, and adjust brightness and colour. Changes show in the preview.',
	})}
/>

<CameraStreamPreview {device} />

<div class="camera-picture-settings">
	<Container padding="s" type="borderSolid" backgroundColor="color-base">
		<Text styleType="h2" class="mb-s">
			{$format('settings_heading_camera_orientation', { default: 'Orientation' })}
		</Text>
		<Text styleType="body-small" class="mb-s text-color-muted">
			{$format('settings_desc_camera_orientation', {
				default: 'Rotate or mirror the image to match how the camera is mounted.',
			})}
		</Text>
		<ToggleV2
			size="m"
			class="mb-s"
			bind:checked={picture.flip}
			disabled={loading}
			id="toggle-camera-picture-flip"
		>
			{$format('camera_video_flip', { default: 'Flip image' })}
		</ToggleV2>
		<ToggleV2
			size="m"
			bind:checked={picture.mirror}
			disabled={loading}
			id="toggle-camera-picture-mirror"
		>
			{$format('camera_video_mirror', { default: 'Mirror image' })}
		</ToggleV2>
	</Container>

	{#if supportsAntiflicker}
		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('camera_video_antiflicker', { default: 'Anti-flicker' })}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('camera_video_antiflicker_hint', {
					default: 'Match your mains frequency to stop artificial lighting flickering on video.',
				})}
			</Text>
			<div class="d-flex">
				<TokenV2
					id="token-camera-antiflicker-50"
					selected={picture.antiflicker === '50Hz'}
					size="m"
					title={$format('camera_video_antiflicker_50', { default: '50 Hz' })}
					class="mr-s"
					disabled={loading}
					on:click={() => {
						picture = { ...picture, antiflicker: '50Hz' };
					}}
				/>
				<TokenV2
					id="token-camera-antiflicker-60"
					selected={picture.antiflicker === '60Hz'}
					size="m"
					title={$format('camera_video_antiflicker_60', { default: '60 Hz' })}
					disabled={loading}
					on:click={() => {
						picture = { ...picture, antiflicker: '60Hz' };
					}}
				/>
			</div>
		</Container>
	{/if}

	{#each VIDEO_SLIDERS as { key, title, description, min, max, lineMarkers } (key)}
		<div class="mb-l">
			<Text element="h2" styleType="h2" class="mb-xs">{title}</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">{description}</Text>
			<SliderV2
				id="slider-camera-picture-{key}"
				value={picture[key]}
				on:value_change={(e) => {
					picture = { ...picture, [key]: e.detail.value };
				}}
				disabled={loading}
				{min}
				{max}
				step={1}
				type="incremented"
				unit=""
				{lineMarkers}
				withOverlay={false}
				a11yProps={{
					ariaLabel: title,
					ariaLabelValue: `${title} ${$format('label_value', { default: 'value' })}`,
				}}
			/>
		</div>
	{/each}

	<ButtonV2
		size="m"
		width="full"
		variant="secondary"
		{loading}
		disabled={loading}
		on:click={resetToDefaults}
	>
		{capFirstChar($format('button_reset_to_defaults', { default: 'Reset to defaults' }))}
	</ButtonV2>
</div>

<style>
	.camera-picture-settings {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
